import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../../services/ApiService";
import { useAppContext } from "../../../context/AppContext";
import Swal from "sweetalert2";
import Sidebar from "../../../components/Sidebar";
import SignatureCanvas from "react-signature-canvas";
import SectionLoader from "../../../components/SectionLoader";
import BackButton from "../../../components/BackButton";
import { getOrCreateDeviceId } from "../../../utils/deviceId";
import { isDriverVisibleMaterial } from "../../../utils/lineItemFilters";

const DigitalSignature: React.FC = () => {
  const deliveryID = localStorage.getItem("delivery_id");
  const vendor_id = localStorage.getItem("vendor_id");
  const sales_order_id = localStorage.getItem("sales_order_id");
  const api_token = localStorage.getItem("_token");
  const [showReason, setShowReason] = useState(false);
  const [isfileUploaded, setIsFileUploaded] = useState<boolean>(false);
  const [oldFile, setOldFile] = useState<string>("");
  const [reasonText, setReasonText] = useState<string | null>(null);
  const [signerName, setSignerName] = useState<string | null>(null);
  const navigate = useNavigate();
  const {
    purchaseOrder,
    delivery,
    setDelivery,
    photos,
    setPhotos,
    setPurchaseOrder,
    setToken,
    salesOrder,
    setSalesOrder,
    loads,
    setLoads,
    setAttachments,
    setLoadItems,
    setMeterialSo,
    setMaterialfo,
    progressDeliveries,
    setProgressDeliveries,
  } = useAppContext();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentDeliveryNumber, setCurrentDeliveryNumber] = useState<number>(1);
  const sigCanvas = useRef<SignatureCanvas>(null);

  // Fresh data fetch on load
  useEffect(() => {
    const fetchFreshData = async () => {
      if (!api_token || !sales_order_id) {
        setIsLoading(false);
        return;
      }

      if (delivery && delivery.id && delivery.status) {
        // Validate delivery status - should be 'reached', 'delivered', or 'completed' for signature page
        if (!["reached", "delivered", "completed"].includes(delivery.status)) {
          if (delivery.status === "picked_up") {
            navigate("/upload-weight-ticket");
          } else if (delivery.status === "assigned") {
            navigate("/pick-up");
          } else if (delivery.status === "open") {
            navigate("/");
          }
          setIsLoading(false);
          return;
        }
        setIsLoading(false);
        return;
      }

      if (vendor_id && sales_order_id) {
        try {
          const deviceId = getOrCreateDeviceId();
          const responseData = await ApiService.getFreshData({
            vendor_id: vendor_id,
            sales_order_id: sales_order_id,
            delivery_id: deliveryID || null,
            token: false,
            device_id: deviceId,
          });

          if (responseData && responseData.success) {
            setPurchaseOrder(responseData.data.purchase_order);
            setToken(responseData.api_token);
            setDelivery(responseData.data.delivery);
            setLoads(responseData.data.loads);
            setLoadItems(responseData.data.item_loads);
            setPhotos(responseData.data.photos || []);
            setAttachments(responseData.data.attachments || []);
            setSalesOrder(responseData.data.sales_order);
            setMeterialSo(responseData.data.meterial_so);
            setMaterialfo(responseData.data.material_fo);
            setProgressDeliveries(responseData.data.progress);

            if (responseData.data.delivery) {
              const deliveryStatus = responseData.data.delivery.status;
              if (
                !["reached", "delivered", "completed"].includes(deliveryStatus)
              ) {
                if (deliveryStatus === "picked_up") {
                  navigate("/upload-weight-ticket");
                } else if (deliveryStatus === "assigned") {
                  navigate("/pick-up");
                } else if (deliveryStatus === "open") {
                  navigate("/");
                }
              }
            }
          }
        } catch (error: any) {
          console.error("Error fetching fresh data:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchFreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api_token, vendor_id, sales_order_id]);

  // Load delivery number from localStorage (set when delivery started)
  useEffect(() => {
    const savedDeliveryNumber = localStorage.getItem("delivery_number");
    if (savedDeliveryNumber) {
      setCurrentDeliveryNumber(parseInt(savedDeliveryNumber, 10));
    }
  }, []);

  useEffect(() => {
    if (sigCanvas.current) {
      if (showReason) {
        sigCanvas.current.off();
        sigCanvas.current.clear();
        setSignerName("");
      } else {
        sigCanvas.current.on();
      }
    }
  }, [showReason]);

  useEffect(() => {
    if (!delivery?.id) return;

    let oldDigitalSignature = [];
    if (photos && Array.isArray(photos)) {
      oldDigitalSignature = photos.filter(
        (field: any) =>
          field.photo_type === "signature" && field.delivery_id === delivery.id
      );
    }

    if (oldDigitalSignature.length !== 0) {
      if (oldDigitalSignature[0].file_url !== null) {
        setOldFile(oldDigitalSignature[0].file_url);
      }
      setIsFileUploaded(true);
    }
  }, [navigate, photos, delivery?.id]);

  if (isLoading || !purchaseOrder) return <SectionLoader />;

  const SubmitFileUploadTicket = async () => {
    setButtonLoading(true);

    // Use delivery from context or fallback to localStorage
    const currentDeliveryId = delivery?.id || deliveryID;
    if (!currentDeliveryId) {
      Swal.fire({
        title: "Error",
        text: "Delivery ID not found. Please refresh the page.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setButtonLoading(false);
      return;
    }

    // Check if delivery is already completed or delivered
    if (delivery && (delivery.status === "completed" || delivery.status === "delivered")) {
      Swal.fire({
        title: "Error",
        text: "This delivery is already completed.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setButtonLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("delivery_id", currentDeliveryId);
    formData.append("photo_type", "signature");

    if (showReason) {
      if (!reasonText || reasonText.split(" ").length < 1) {
        Swal.fire({
          title: "Error",
          text: "Please enter a clear reason (at least 1 word).",
          icon: "error",
          confirmButtonText: "OK",
        });
        setButtonLoading(false);
        return;
      }

      formData.append("reason", reasonText);
    } else {
      if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
        Swal.fire({
          title: "Error",
          text: "Please sign the ticket.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setButtonLoading(false);
        return;
      }

      if (!signerName || signerName.trim() === "") {
        Swal.fire({
          title: "Error",
          text: "Please enter signer's first name.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setButtonLoading(false);
        return;
      }

      const dataURL = sigCanvas.current.toDataURL("image/png");

      if (!dataURL) {
        Swal.fire({
          title: "Error",
          text: "Failed to capture signature.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setButtonLoading(false);
        return;
      }

      const response = await fetch(dataURL);
      const blob = await response.blob();
      const file = new File([blob], "signature.png", { type: "image/png" });

      // Validate file size (max 5MB for signature)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          title: "Error",
          text: "Signature file size too large. Please try again with a smaller signature.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setButtonLoading(false);
        return;
      }

      formData.append("file", file);
      formData.append("name", signerName);
      formData.append("vendor_id", vendor_id || "");
    }

    try {
      const response = await ApiService.SubmitSignature(formData);
      console.log("Upload response:", response.data);

      if (response && response.success) {
        setPhotos(response.data);
        Swal.fire({
          title: "Success!",
          text: response.message,
          icon: "success",
          confirmButtonText: "Next",
        }).then(() => {
          localStorage.setItem("page", "complete-delivery");
          navigate("/complete-delivery");
        });
      } else {
        setButtonLoading(false);
        Swal.fire({
          title: "Error!",
          text: response.message,
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      setButtonLoading(false);
      Swal.fire({
        title: "Error",
        text: "Submission failed. Try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const clear = () => {
    sigCanvas.current?.clear();
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <Sidebar />

        <div className="col-lg-9 col-12 content-area">
          <div className="row">
            <div className="col-12 mb-3">
              <BackButton />
            </div>
          </div>
          {!isfileUploaded && (
            <div className="row">
              <div className="col-12 m-auto mb-4">
                <div className="box py-1">
                  <h5 className="mb-0 text-center fw-semibold">
                    Hand your phone to the jobsite contact and ask them to sign
                    using their finger
                  </h5>
                  {/* {salesOrder?.order_items?.filter((item: any) => item.unit !== "Load").length > 0 ? (
                    <>
                      <p className="text-center mt-2 mb-0 fw-semibold" style={{ color: "#df7c00" }}>
                        You are signing for 1 load of<br />"{salesOrder.order_items.filter((item: any) => item.unit !== "Load").map((item: any) => item.description).join(", ")}"
                      </p>
                      <p className="text-center mt-1 mb-0 fw-normal" style={{ color: "#098148", fontSize: "0.9rem" }}>
                        Delivery {currentDeliveryNumber} of {loads?.total_loads || currentDeliveryNumber}
                      </p>
                    </>
                  ) : (
                    <p className="text-center mt-2 mb-0 fw-normal" style={{ color: "#df7c00" }}>
                      To acknowledge receipt of delivery {currentDeliveryNumber}
                    </p>
                  )} */}
                  {(() => {
  // 1) Prefer what's stored on the delivery record (most reliable, survives refresh)
  let pickedUp: string[] = [];
  const rawFromDelivery = (delivery as any)?.picked_up_material;
  if (rawFromDelivery) {
    try {
      pickedUp = Array.isArray(rawFromDelivery)
        ? rawFromDelivery
        : JSON.parse(rawFromDelivery);
    } catch {
      pickedUp = [];
    }
  }

  // 2) Fall back to what was saved at pickup time
  if (pickedUp.length === 0) {
    const saved = localStorage.getItem("picked_up_material");
    if (saved) {
      try {
        pickedUp = JSON.parse(saved);
      } catch {
        pickedUp = [];
      }
    }
  }

  // 3) Final fallback — all non-"Load" items on the SO (old behavior)
  // const nonLoadItems =
  //   salesOrder?.order_items?.filter((item: any) => item.unit !== "Load") || [];

  // const materialsToShow =
  //   pickedUp.length > 0
  //     ? pickedUp
  //     : nonLoadItems.map((item: any) => item.description);
// 3) Final fallback — only REAL materials on the SO (hides service/misc)
  const materialItems =
    salesOrder?.order_items?.filter(isDriverVisibleMaterial) || [];

  const salesOrderMaterials = materialItems
    .map((item: any) => item.description)
    .filter(Boolean);

  const materialsToShow =
    salesOrderMaterials.length > 0
      ? salesOrderMaterials
      : pickedUp;
  if (materialsToShow.length === 0) {
    return (
      <p className="text-center mt-2 mb-0 fw-normal" style={{ color: "#df7c00" }}>
        To acknowledge receipt of delivery {currentDeliveryNumber}
      </p>
    );
  }

  return (
    <>
      <p className="text-center mt-2 mb-0 fw-semibold" style={{ color: "#df7c00" }}>
        You are signing for 1 load of
        <br />
        {materialsToShow.map((m: string) => `"${m}"`).join(", ")}
      </p>
      <p
        className="text-center mt-1 mb-0 fw-normal"
        style={{ color: "#098148", fontSize: "0.9rem" }}
      >
        Delivery {currentDeliveryNumber} of{" "}
        {loads?.total_loads || currentDeliveryNumber}
      </p>
    </>
  );
})()}
                </div>
              </div>

              <div className="col-md-12 m-auto mb-1">
                <div className="box py-1">
                  <h6 className="mb-3 text-start fw-semibold">
                    Signer First Name <span className="text-danger">*</span>
                  </h6>
                  <input
                    type="text"
                    disabled={showReason}
                    value={signerName || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                      setSignerName(value);
                    }}
                    placeholder="Enter First Name"
                    className="form-control"
                  />
                </div>
              </div>
              <div className="col-md-12 m-auto mb-1">
                <div className="signature">
                  <div id="DigitalSignature">
                    <p className="placeholders">Jobsite signature</p>
                    {/* <SignatureCanvas
                      penColor="black"
                      ref={sigCanvas}
                      canvasProps={{
                        width: 500,
                        height: 220,
                        className: "sigCanvas border",
                      }}
                    /> */}
                    <SignatureCanvas
                      penColor="black"
                      ref={sigCanvas}
                      clearOnResize={false}
                      canvasProps={{
                        className: "sigCanvas border",
                      }}
                    />
                  </div>
                </div>
                <div className="box mt-2">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={showReason}
                          onChange={(e) => setShowReason(e.target.checked)}
                          id="NoOneAvailable"
                          name="no_one_available"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="NoOneAvailable"
                        >
                          No one is Available
                        </label>
                      </div>
                    </div>
                    <div className="col-4 text-end">
                      <button
                        className="clear-btn"
                        onClick={clear}
                        type="button"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {showReason && (
                <div className="col-md-12 m-auto mt-3 mb-4 no_one_available_box">
                  <div className="box py-3 px-3">
                    <h4 className="mb-2">Enter Reason</h4>
                    <p className="mb-3">
                      Please provide a clear reason why signature is not
                      captured.
                    </p>
                  </div>
                  <div className="signature mt-3">
                    <label htmlFor="reason"></label>
                    <textarea
                      value={reasonText || ""}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Enter Reason..."
                      className="form-control"
                      rows={4}
                    ></textarea>
                  </div>
                </div>
              )}
            </div>
          )}
          {isfileUploaded && (
            <div className="row">
              <div className="col-12 m-auto mb-4">
                <div className="box py-1 px-1">
                  <div className="col-12">
                    <h4 className="mb-3">Already Signed</h4>
                    <img
                      src={oldFile}
                      alt="Signature"
                      className="img-fluid"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 mb-4 d-flex row m-auto flex-wrap align-items-center justify-content-center gap-3" style={{ position: 'relative', zIndex: 10, paddingBottom: '20px' }}>
            <div className="col-12">
              {!isfileUploaded && (
                <button
                  disabled={buttonLoading}
                  onClick={() => SubmitFileUploadTicket()}
                  className="btn-orange"
                  style={{ position: 'relative', zIndex: 10 }}
                >
                  {buttonLoading && (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {showReason ? "Submit" : "Upload Signature"}
                </button>
              )}
              {isfileUploaded && (
                <button
                  disabled={buttonLoading}
                  onClick={() => navigate("/complete-delivery")}
                  className="btn-orange"
                  style={{ position: 'relative', zIndex: 10 }}
                >
                  {buttonLoading && (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default DigitalSignature;
